import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { getRenewalTasks, RenewalTask, getClientDetails } from '../src/Utils/renewalTaskApi';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, TrendingUp, Building2, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../src/routes/config';

interface ClientRiskSummary {
  clientId: string;
  clientName: string;
  totalTasks: number;
  overdueTasks: number;
  criticalTasks: number;
  highPriorityTasks: number;
  riskScore: number;
}

export const SupervisorRiskView: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientRiskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [clientTasks, setClientTasks] = useState<RenewalTask[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Fetch all tasks to calculate risk
  useEffect(() => {
    const fetchRiskData = async () => {
      if (!token) return;

      setLoading(true);
      try {
        // Get all tasks (no filters)
        const response = await getRenewalTasks({}, token);
        
        if (response.success) {
          const tasks = response.data.tasks;
          
          // Group by client and calculate risk
          const clientMap = new Map<string, ClientRiskSummary>();

          tasks.forEach((task) => {
            if (!clientMap.has(task.clientId)) {
              clientMap.set(task.clientId, {
                clientId: task.clientId,
                clientName: task.clientName,
                totalTasks: 0,
                overdueTasks: 0,
                criticalTasks: 0,
                highPriorityTasks: 0,
                riskScore: 0,
              });
            }

            const client = clientMap.get(task.clientId)!;
            client.totalTasks++;

            // Count overdue (bucket = OVERDUE or dueDate < today)
            if (task.bucket === 'OVERDUE' || 
                (task.dueDate && new Date(task.dueDate) < new Date())) {
              client.overdueTasks++;
            }

            // Count critical (P0)
            if (task.priority === 'P0') {
              client.criticalTasks++;
            }

            // Count high priority (P0 or P1)
            if (task.priority === 'P0' || task.priority === 'P1') {
              client.highPriorityTasks++;
            }
          });

          // Calculate risk score for each client
          const clientsWithRisk = Array.from(clientMap.values()).map((client) => {
            // Risk score: weighted combination of overdue, critical, and high priority tasks
            const riskScore = 
              client.overdueTasks * 10 +  // Overdue tasks are highest risk
              client.criticalTasks * 8 +   // Critical priority
              client.highPriorityTasks * 3; // High priority
            
            return {
              ...client,
              riskScore,
            };
          });

          // Sort by risk score (highest first)
          clientsWithRisk.sort((a, b) => b.riskScore - a.riskScore);

          setClients(clientsWithRisk);
        } else {
          toast.error('Failed to load risk data');
        }
      } catch (error: any) {
        console.error('Error fetching risk data:', error);
        toast.error(error.message || 'Failed to load risk data');
      } finally {
        setLoading(false);
      }
    };

    fetchRiskData();
  }, [token]);

  const handleClientClick = async (clientId: string) => {
    if (!token) return;

    setSelectedClient(clientId);
    setLoadingTasks(true);

    try {
      const response = await getClientDetails(clientId, token);
      
      if (response.success) {
        // Show tasks sorted by priority and due date
        const sortedTasks = response.data.allTasks.sort((a, b) => {
          // First by priority (P0 first)
          const priorityOrder = { P0: 0, P1: 1, P2: 2, P3: 3, P4: 4 };
          const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
          if (priorityDiff !== 0) return priorityDiff;

          // Then by due date (overdue first)
          if (a.dueDate && b.dueDate) {
            return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          }
          if (a.dueDate) return -1;
          if (b.dueDate) return 1;
          return 0;
        });

        setClientTasks(sortedTasks);
      } else {
        toast.error('Failed to load client tasks');
      }
    } catch (error: any) {
      console.error('Error fetching client tasks:', error);
      toast.error(error.message || 'Failed to load client tasks');
    } finally {
      setLoadingTasks(false);
    }
  };

  const getRiskBadge = (riskScore: number) => {
    if (riskScore >= 50) {
      return <Badge className="bg-red-100 text-red-800 border-red-300">Critical</Badge>;
    } else if (riskScore >= 20) {
      return <Badge className="bg-orange-100 text-orange-800 border-orange-300">High</Badge>;
    } else if (riskScore >= 10) {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Medium</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800 border-gray-300">Low</Badge>;
    }
  };

  const getPriorityBadge = (priority: RenewalTask['priority']) => {
    const variants: Record<string, { className: string; label: string }> = {
      P0: { className: 'bg-red-100 text-red-800 border-red-300', label: 'P0' },
      P1: { className: 'bg-orange-100 text-orange-800 border-orange-300', label: 'P1' },
      P2: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'P2' },
      P3: { className: 'bg-blue-100 text-blue-800 border-blue-300', label: 'P3' },
      P4: { className: 'bg-gray-100 text-gray-800 border-gray-300', label: 'P4' },
    };

    const config = variants[priority] || variants.P4;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Supervisor Risk View</h1>
          <p className="text-sm text-gray-600 mt-1">
            High-risk clients sorted by overdue and critical items
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.renewalTasks)}
        >
          View All Tasks
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-600">Loading risk data...</span>
          </CardContent>
        </Card>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-sm text-gray-600">No clients with risk data found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* High-Risk Clients List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  High-Risk Clients
                </CardTitle>
                <CardDescription>
                  Clients sorted by risk score (overdue and critical tasks)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead className="text-center">Total Tasks</TableHead>
                        <TableHead className="text-center">Overdue</TableHead>
                        <TableHead className="text-center">Critical (P0)</TableHead>
                        <TableHead className="text-center">High Priority</TableHead>
                        <TableHead className="text-center">Risk Score</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow
                          key={client.clientId}
                          className={selectedClient === client.clientId ? 'bg-blue-50' : ''}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <div>
                                <div className="font-medium">{client.clientName}</div>
                                <div className="text-xs text-gray-500">{client.clientId}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{client.totalTasks}</TableCell>
                          <TableCell className="text-center">
                            {client.overdueTasks > 0 ? (
                              <Badge variant="destructive">{client.overdueTasks}</Badge>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {client.criticalTasks > 0 ? (
                              <Badge className="bg-red-100 text-red-800 border-red-300">
                                {client.criticalTasks}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {client.highPriorityTasks > 0 ? (
                              <Badge variant="outline">{client.highPriorityTasks}</Badge>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {getRiskBadge(client.riskScore)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleClientClick(client.clientId)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Client Tasks Detail */}
          <div className="lg:col-span-1">
            {selectedClient && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">
                    {clients.find(c => c.clientId === selectedClient)?.clientName || 'Client Tasks'}
                  </CardTitle>
                  <CardDescription>
                    Tasks sorted by priority and due date
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingTasks ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : clientTasks.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No tasks found</p>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {clientTasks.map((task) => (
                        <div
                          key={task.id}
                          className="p-3 border rounded-md hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`${ROUTES.renewalTasks}?taskId=${task.id}`)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{task.itemName}</div>
                              <div className="text-xs text-gray-500">{task.sectionName}</div>
                            </div>
                            {getPriorityBadge(task.priority)}
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{task.status}</span>
                            {task.dueDate && (
                              <span>{format(new Date(task.dueDate), 'MMM dd')}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

